import React, { useCallback, useEffect, useRef, useState } from 'react';
import { orderBy } from 'lodash';
import { Link, Redirect, RouteComponentProps } from 'react-router-dom';
import * as Yup from 'yup';
import { DOMAIN_ORGANIZATION, DOMAIN_ORGANIZATION_LOWER } from '../../constants/domainCopy';

import * as Core from '../../core';
import FormField from '../../components/formField';
import InfoMessage from '../../components/infoMessage';
import RegistrationForm, { RegistrationFormValues } from '../../components/registrationForm';
import RegistrationHeader from '../../components/registrationHeader';
import withLoading, { WithLoadingProps } from '../../components/withLoading';
import { useLeague, useLeagueConfiguration } from '../../hooks/store';
import { OrganizationService } from '../../services/organizationService';
import { UserService } from '../../services/userService';

interface CreateOrganizationPageProps extends WithLoadingProps, RouteComponentProps<{ inviteCode: string }> {}

export interface CreateOrganizationFormValues extends RegistrationFormValues {
    availableOrganizationId?: string;
    inviteCode: string;
    organizationName?: string;
}

const CreateOrganizationPage = (props: CreateOrganizationPageProps): JSX.Element => {
    const {
        match: {
            params: { inviteCode },
        },
        setError,
        setIsLoading,
    } = props;

    const league = useLeague();
    
    const leagueConfiguration = useLeagueConfiguration();

    const [selectedOrganizationId, setSelectedOrganizationId] = useState<string | undefined>(undefined);
    const [organizationOptions, setOrganizationOptions] = useState<Core.Models.AvailableOrganization[] | undefined>(
        undefined
    );
    const [existingOrganization, setExistingOrganization] = useState<Core.Models.RegisteredOrganizationLookup>();
    const [isCheckingOrganization, setIsCheckingOrganization] = useState(false);
    const lookupRequest = useRef(0);
    const nameLookupTimer = useRef<number>();

    const lookupRegisteredOrganization = useCallback(
        async (query: { availableOrganizationId?: string; organizationName?: string }) => {
            const request = ++lookupRequest.current;
            const organizationName = query.organizationName?.trim();
            if (!query.availableOrganizationId && !organizationName) {
                setExistingOrganization(undefined);
                setIsCheckingOrganization(false);
                return;
            }

            setIsCheckingOrganization(true);
            try {
                const result = await OrganizationService.findRegisteredOrganization({
                    availableOrganizationId: query.availableOrganizationId || undefined,
                    inviteCode,
                    organizationName: organizationName || undefined,
                });
                if (request !== lookupRequest.current) return;
                setExistingOrganization(result?.id ? result : undefined);
            } catch {
                if (request !== lookupRequest.current) return;
                setExistingOrganization(undefined);
            } finally {
                if (request === lookupRequest.current) setIsCheckingOrganization(false);
            }
        },
        [inviteCode]
    );

    useEffect(() => () => window.clearTimeout(nameLookupTimer.current), []);

    useEffect(() => {
        (async () => {
            try {
                const response = await OrganizationService.getOrganizationsForInviteCode(inviteCode);
                setOrganizationOptions(response);
            } catch (e) {
                setError(e);
            } finally {
                setIsLoading(false);
            }
        })();
    }, [inviteCode, setError, setIsLoading]);

    const renderOrganizationFieldGroup = useCallback((): JSX.Element => {
        if (!league) return <></>;

        const contactUs = (!!league.phoneNumber || !!league.email) && (
            <>
                Contact us at {league.phoneNumber && <em>{league.phoneNumber}</em>}
                {league.phoneNumber && league.email && ' or '}
                {league.email && (
                    <em>
                        <a href={`mailto:${league.email}`}>{league.email}</a>
                    </em>
                )}
            </>
        );

        const showAddress = (org: Core.Models.AvailableOrganization): boolean => {
            if (!org.streetAddress && !org.city && !org.state && !org.postalCode && !org.phoneNumber) return false;
            return true;
        };

        if (!!organizationOptions && organizationOptions.length) {
            const selectedOrganization = selectedOrganizationId
                ? organizationOptions.filter((o) => o.id === selectedOrganizationId)[0]
                : undefined;
            return (
                <>
                    <fieldset className="form-group form-group create-organization">
                        <FormField
                            className="create-organization__organization"
                            component="select"
                            description={`Select ${DOMAIN_ORGANIZATION}`}
                            name="availableOrganizationId"
                            onChange={(e: React.ChangeEvent<HTMLSelectElement>) => {
                                setSelectedOrganizationId(e.target.value);
                                lookupRegisteredOrganization({
                                    availableOrganizationId: e.target.value || undefined,
                                });
                            }}
                        >
                            <option key={-1} value={''} disabled>
                                Select {DOMAIN_ORGANIZATION}
                            </option>
                            {orderBy(organizationOptions, (i) => i.name).map(
                                (organization: Core.Models.AvailableOrganization, index: number) => {
                                    return (
                                        <option key={index} value={organization.id}>
                                            {organization.name}
                                        </option>
                                    );
                                }
                            )}
                        </FormField>
                        {selectedOrganization && showAddress(selectedOrganization) && (
                            <div className="create-organization__selected-organization">
                                <p className="create-organization__selected-organization__line1">
                                    {selectedOrganization.streetAddress}, {selectedOrganization.city},{' '}
                                    {selectedOrganization.state} {selectedOrganization.postalCode}
                                </p>
                                {!!selectedOrganization.phoneNumber && (
                                    <p className="create-organization__selected-organization__line2">
                                        {selectedOrganization.phoneNumber}
                                    </p>
                                )}
                            </div>
                        )}
                        <DuplicateOrganizationNotice organization={existingOrganization} />
                    </fieldset>
                    <div className="create-organization__info-wrong">
                        {!!selectedOrganization
                            ? contactUs && (
                                  <p>
                                      {DOMAIN_ORGANIZATION} information incorrect, or need help registering? {contactUs}.
                                  </p>
                              )
                            : contactUs && (
                                  <p>
                                      Don't see your {DOMAIN_ORGANIZATION_LOWER}, or need help registering?{' '}
                                      {contactUs}.
                                  </p>
                              )}
                    </div>
                </>
            );
        } else {
            return (
                <fieldset className="form-group">
                    <FormField
                        type="text"
                        description={`${DOMAIN_ORGANIZATION} Name`}
                        name="organizationName"
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                            window.clearTimeout(nameLookupTimer.current);
                            const organizationName = e.target.value;
                            lookupRequest.current += 1;
                            setExistingOrganization(undefined);
                            if (!organizationName.trim()) {
                                setIsCheckingOrganization(false);
                                return;
                            }

                            setIsCheckingOrganization(true);
                            nameLookupTimer.current = window.setTimeout(() => {
                                lookupRegisteredOrganization({ organizationName });
                            }, 400);
                        }}
                    />
                    <DuplicateOrganizationNotice organization={existingOrganization} />
                </fieldset>
            );
        }
    }, [existingOrganization, league, lookupRegisteredOrganization, organizationOptions, selectedOrganizationId]);

    // form should be unavailable for Club Edition leagues
    if (league?.edition === Core.Models.LeagueEdition.Club) {
        return <Redirect to="/" />;
    }

    if (!league) return <></>;
    if (!organizationOptions) return <></>;
    return (
        <div>
            <RegistrationForm<CreateOrganizationFormValues>
                acceptEmail={true}
                header={
                    <>
                        <RegistrationHeader
                            formInstructions="All fields are required."
                            message={`Participate in ${
                                league.name
                            }'s official community! Register your ${DOMAIN_ORGANIZATION_LOWER} today by filling in the information below.`}
                            welcome={`Welcome to ${league.name}`}
                        />
                        {renderOrganizationFieldGroup()}
                    </>
                }
                initialValues={{
                    availableOrganizationId: '',
                    inviteCode,
                    organizationName: '',
                }}
                leagueFee={leagueConfiguration?.leagueFee}
                register={async (values: CreateOrganizationFormValues) => {
                    const payload = {
                        ...values,
                        availableOrganizationId: !!values.availableOrganizationId
                            ? values.availableOrganizationId
                            : undefined,
                        organizationName: !!values.organizationName ? values.organizationName : undefined,
                    };

                    return await UserService.createOrganization(payload);
                }}
                registrationAction={Core.Models.RegistrationAction.CreateOrganization}
                schema={{
                    availableOrganizationId: Yup.string().when('organizationName', {
                        is: (organizationName?: string) => !organizationName,
                        then: Yup.string().required(`${DOMAIN_ORGANIZATION} is required`),
                        otherwise: Yup.string(),
                    }),
                    inviteCode: Yup.string().required('Invite code is required'),
                    organizationName: Yup.string().when('availableOrganizationId', {
                        is: (availableOrganizationId?: string) => !availableOrganizationId,
                        then: Yup.string()
                            .required(`${DOMAIN_ORGANIZATION} is required`)
                            .max(
                                Core.Constants.ORGANIZATION_NAME_MAX_LENGTH,
                                `${DOMAIN_ORGANIZATION} name must be ${Core.Constants.ORGANIZATION_NAME_MAX_LENGTH} characters or fewer`
                            ),
                        otherwise: Yup.string(),
                    }),
                }}
                schemaDependentFields={['availableOrganizationId', 'organizationName']}
                submitDisabled={!!existingOrganization?.id || isCheckingOrganization}
                submitText={`Create ${DOMAIN_ORGANIZATION}`}
            />
        </div>
    );
};

const DuplicateOrganizationNotice = ({
    organization,
}: {
    organization?: Core.Models.RegisteredOrganizationLookup;
}): JSX.Element => {
    if (!organization?.id) return <></>;

    return (
        <InfoMessage
            className="mt2x"
            message={
                <div>
                    An organization with this name has already been registered for this league.{' '}
                    <Link to={`/organizations/${organization.id}`}>
                        View {organization.name || 'organization'}
                    </Link>
                </div>
            }
            type="error"
        />
    );
};

export default withLoading(CreateOrganizationPage, {
    errorDisplayPageProps: { fullPage: false },
    loadingProps: { blockItem: true },
    showNotFoundFor404: true,
});
